import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./viewFranchiseManagerAccessControl.css";
import {
  getFranchiseManagerDetail,
  updateFranchiseManagerAccessControl,
  deleteFranchiseManagerAccessControl,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import AddAccessControlModal from "./addAccessControlModal";

const PERMISSIONS = [
  { key: "read", label: "View" },
  { key: "write", label: "Create" },
  { key: "update", label: "Edit" },
  { key: "delete", label: "Delete" },
];

const humanize = (str = "") => str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const countGranted = (row) => PERMISSIONS.reduce((n, p) => n + (row[p.key] ? 1 : 0), 0);

const sectionTotals = (section) => {
  const rows = [section, ...(section.sub_access || [])];
  const granted = rows.reduce((n, r) => n + countGranted(r), 0);
  const possible = rows.length * PERMISSIONS.length;
  return { granted, possible };
};

const ViewFranchiseManagerAccessControl = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { franchise_id, city_head_id, locality_head_id } = params;
  const id = params.id || locality_head_id || city_head_id;

  const viewPath = locality_head_id
    ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads/${locality_head_id}/view`
    : city_head_id
      ? `/franchise/${franchise_id}/city-heads/${city_head_id}/view`
      : `/franchise/view/${id}`;

  const listPath = locality_head_id
    ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads`
    : city_head_id
      ? `/franchise/${franchise_id}/city-heads`
      : "/franchise";

  const {
    franchiseManagerDetail,
    detailLoading = false,
    accessControlUpdating = false,
  } = useSelector((state) => state.franchiseManager || {});

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [accessControl, setAccessControl] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [sidebarSections, setSidebarSections] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  // Tracks which row is currently being deleted: a section key, or
  // "section::sub_section" for a sub-row. Used to scope the spinner/disabled
  // state to just that row instead of the whole page.
  const [deletingKey, setDeletingKey] = useState(null);
  // Drives the custom confirm popup (replaces window.confirm). Shape:
  // { type: "section" | "sub", sectionIdx, subIdx, title, message } | null
  const [confirmTarget, setConfirmTarget] = useState(null);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/setting/view`, { headers: { "Content-Type": "application/json" } })
      .then((res) => setSidebarSections(res.data?.data?.franchise_sidebar_sections || []))
      .catch(() => toast.error("Failed to load sidebar sections"));
  }, [BASE_URL]);

  useEffect(() => {
    if (id) {
      dispatch(getFranchiseManagerDetail(id)).catch(() => {
        toast.error("Couldn't load this manager's access settings");
      });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (franchiseManagerDetail?.access_control) {
      setAccessControl(
        franchiseManagerDetail.access_control.map((section) => ({
          ...section,
          sub_access: (section.sub_access || []).map((sub) => ({ ...sub })),
        }))
      );
      setDirty(false);
    }
  }, [franchiseManagerDetail]);

  const data = franchiseManagerDetail;

  const overall = useMemo(() => {
    return accessControl.reduce(
      (acc, section) => {
        const { granted, possible } = sectionTotals(section);
        return { granted: acc.granted + granted, possible: acc.possible + possible };
      },
      { granted: 0, possible: 0 }
    );
  }, [accessControl]);

  const missingSections = useMemo(
    () => sidebarSections.filter((s) => !accessControl.some((ac) => ac.section === s.key)),
    [sidebarSections, accessControl]
  );

  const updateSection = (sectionIdx, updater) => {
    setAccessControl((prev) => {
      const next = [...prev];
      next[sectionIdx] = updater({ ...next[sectionIdx] });
      return next;
    });
    setDirty(true);
  };

  const togglePermission = (sectionIdx, key) => {
    if (!canEdit) return;
    updateSection(sectionIdx, (section) => ({ ...section, [key]: !section[key] }));
  };

  const toggleSubPermission = (sectionIdx, subIdx, key) => {
    if (!canEdit) return;
    updateSection(sectionIdx, (section) => {
      const subs = [...section.sub_access];
      subs[subIdx] = { ...subs[subIdx], [key]: !subs[subIdx][key] };
      return { ...section, sub_access: subs };
    });
  };

  const setAllForSection = (sectionIdx, enable) => {
    if (!canEdit) return;
    updateSection(sectionIdx, (section) => {
      const patched = { ...section };
      PERMISSIONS.forEach((p) => (patched[p.key] = enable));
      patched.sub_access = section.sub_access.map((sub) => {
        const s = { ...sub };
        PERMISSIONS.forEach((p) => (s[p.key] = enable));
        return s;
      });
      return patched;
    });
  };

  const handleAddSections = (sectionsToAdd) => {
    const newEntries = sectionsToAdd.map((s) => ({
      section: s.key,
      read: false,
      write: false,
      update: false,
      delete: false,
      sub_access: (s.sub_sections || []).map((sub) => ({
        sub_section: sub.key,
        read: false,
        write: false,
        update: false,
        delete: false,
      })),
    }));
    setAccessControl((prev) => [...prev, ...newEntries]);
    setDirty(true);
    setAddModalOpen(false);
  };

  const toggleCollapsed = (sectionKey) => {
    setCollapsed((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const handleReset = () => {
    if (!franchiseManagerDetail?.access_control) return;
    setAccessControl(
      franchiseManagerDetail.access_control.map((section) => ({
        ...section,
        sub_access: (section.sub_access || []).map((sub) => ({ ...sub })),
      }))
    );
    setDirty(false);
  };

  const handleSave = () => {
    if (!canEdit) return;

    const payload = accessControl.map(({ _id, sub_access, ...rest }) => ({
      ...rest,
      sub_access: (sub_access || []).map(({ _id, ...subRest }) => subRest),
    }));

    dispatch(updateFranchiseManagerAccessControl({ id, access_control: payload }))
      .then(() => {
        toast.success("Access updated");
        setDirty(false);
      })
      .catch((err) => {
        toast.error(err.message || "Couldn't save access changes");
      });
  };

  // Opens the confirm popup for removing an entire section.
  const handleDeleteSection = (sectionIdx) => {
    if (!canEdit) return;
    const section = accessControl[sectionIdx];
    setConfirmTarget({
      type: "section",
      sectionIdx,
      title: "Remove section access?",
      message: `This removes all permissions for "${humanize(section.section)}", including its sub-sections. This can't be undone.`,
    });
  };

  // Opens the confirm popup for removing a single sub-section.
  const handleDeleteSubSection = (sectionIdx, subIdx) => {
    if (!canEdit) return;
    const section = accessControl[sectionIdx];
    const sub = section.sub_access[subIdx];
    setConfirmTarget({
      type: "sub",
      sectionIdx,
      subIdx,
      title: "Remove sub-section access?",
      message: `This removes all permissions for "${humanize(sub.sub_section)}". This can't be undone.`,
    });
  };

  // Runs after the popup is confirmed. Fires immediately against the API
  // (not staged with the rest of the toggle changes) since the delete
  // endpoint mutates and logs history server-side right away.
  const confirmDelete = () => {
    if (!confirmTarget) return;
    const { type, sectionIdx, subIdx } = confirmTarget;
    const section = accessControl[sectionIdx];

    if (type === "section") {
      setDeletingKey(section.section);
      dispatch(deleteFranchiseManagerAccessControl({ id, section: section.section }))
        .then(() => {
          setAccessControl((prev) => prev.filter((_, idx) => idx !== sectionIdx));
          toast.success("Section access removed");
        })
        .catch((err) => {
          toast.error(err.message || "Couldn't remove section access");
        })
        .finally(() => setDeletingKey(null));
    } else {
      const sub = section.sub_access[subIdx];
      const key = `${section.section}::${sub.sub_section}`;
      setDeletingKey(key);
      dispatch(
        deleteFranchiseManagerAccessControl({
          id,
          section: section.section,
          sub_section: sub.sub_section,
        })
      )
        .then(() => {
          setAccessControl((prev) => {
            const next = [...prev];
            next[sectionIdx] = {
              ...next[sectionIdx],
              sub_access: next[sectionIdx].sub_access.filter((_, i) => i !== subIdx),
            };
            return next;
          });
          toast.success("Sub-section access removed");
        })
        .catch((err) => {
          toast.error(err.message || "Couldn't remove sub-section access");
        })
        .finally(() => setDeletingKey(null));
    }

    setConfirmTarget(null);
  };

  if (detailLoading && !data) {
    return (
      <MainLayout>
        <div className="acl-wrapper">
          <div className="acl-loading">
            <div className="acl-spinner" />
            <span>Loading access settings…</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!detailLoading && !data) {
    return (
      <MainLayout>
        <div className="acl-empty">
          <h3>Franchise manager not found</h3>
          <Link to="/franchise" className="acl-btn acl-btn--primary">Back to list</Link>
        </div>
      </MainLayout>
    );
  }

  const overallPct = overall.possible ? Math.round((overall.granted / overall.possible) * 100) : 0;

  return (
    <MainLayout>
      <div className="acl-wrapper">
        <div className="acl-topbar">
          <button className="acl-back" onClick={() => navigate(-1)} type="button" aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="acl-breadcrumb">
            <Link to={listPath}>Franchise</Link>
            <span>/</span>
            <Link to={viewPath}>{data.name}</Link>
            <span>/</span>
            <span className="acl-breadcrumb-current">Access</span>
          </div>
        </div>

        <header className="acl-header">
          <div className="acl-header-identity">
            <div className="acl-avatar">
              {data.profile_image ? (
                <img src={data.profile_image} alt={data.name} />
              ) : (
                <span>{(data.name || "FM").slice(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div>
              <h1>{data.name}</h1>
              <p>{humanize(data.role)} · {data.email}</p>
            </div>
          </div>

          <div className="acl-header-summary">
            <div className="acl-ring" style={{ "--pct": overallPct }}>
              <span>{overallPct}%</span>
            </div>
            <div className="acl-header-summary-text">
              <strong>{overall.granted} of {overall.possible}</strong>
              <span>permissions granted</span>
            </div>
          </div>
        </header>

        <div className="acl-legend">
          {PERMISSIONS.map((p) => (
            <div className="acl-legend-item" key={p.key}>
              <span className={`acl-dot acl-dot--${p.key}`} />
              {p.label}
            </div>
          ))}
          {canEdit && (
            <button
              type="button"
              className="acl-mini-btn"
              style={{ marginLeft: "auto" }}
              onClick={() => setAddModalOpen(true)}
            >
              + Add access
            </button>
          )}
        </div>

        <div className="acl-sections">
          {accessControl.length === 0 && (
            <div className="acl-card acl-card--empty">
              No sidebar sections are configured yet — add them from Settings first.
            </div>
          )}

          {accessControl.map((section, sectionIdx) => {
            const { granted, possible } = sectionTotals(section);
            const isCollapsed = !!collapsed[section.section];
            const hasSubs = section.sub_access?.length > 0;
            const sectionDeleting = deletingKey === section.section;

            return (
              <div className="acl-card" key={section.section}>
                <div
                  className="acl-card-head"
                  onClick={() => hasSubs && toggleCollapsed(section.section)}
                  role={hasSubs ? "button" : undefined}
                >
                  <div className="acl-card-head-left">
                    {hasSubs && (
                      <svg
                        className={`acl-chevron ${isCollapsed ? "" : "acl-chevron--open"}`}
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    )}
                    <h2>{humanize(section.section)}</h2>
                    <span className="acl-card-count">{granted}/{possible}</span>
                  </div>

                  <div className="acl-card-head-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="acl-mini-btn"
                      onClick={() => setAllForSection(sectionIdx, true)}
                      disabled={!canEdit}
                    >
                      Grant all
                    </button>
                    <button
                      type="button"
                      className="acl-mini-btn acl-mini-btn--muted"
                      onClick={() => setAllForSection(sectionIdx, false)}
                      disabled={!canEdit}
                    >
                      Revoke all
                    </button>
                    <button
                      type="button"
                      className="acl-mini-btn acl-mini-btn--danger"
                      onClick={() => handleDeleteSection(sectionIdx)}
                      disabled={!canEdit || sectionDeleting}
                    >
                      {sectionDeleting ? "Removing…" : "Remove"}
                    </button>
                  </div>
                </div>

                <div className="acl-row acl-row--section">
                  <span className="acl-row-label">Section access</span>
                  <div className="acl-toggles">
                    {PERMISSIONS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={`acl-toggle acl-toggle--${p.key} ${section[p.key] ? "is-on" : ""}`}
                        onClick={() => togglePermission(sectionIdx, p.key)}
                        aria-pressed={!!section[p.key]}
                        title={p.label}
                        disabled={!canEdit}
                      >
                        <span className="acl-toggle-knob" />
                      </button>
                    ))}
                  </div>
                </div>

                {hasSubs && !isCollapsed && (
                  <div className="acl-subrows">
                    {section.sub_access.map((sub, subIdx) => {
                      const subKey = `${section.section}::${sub.sub_section}`;
                      const subDeleting = deletingKey === subKey;
                      return (
                        <div className="acl-row acl-row--sub" key={sub.sub_section}>
                          <span className="acl-row-label">{humanize(sub.sub_section)}</span>
                          <div className="acl-row-right">
                            <div className="acl-toggles">
                              {PERMISSIONS.map((p) => (
                                <button
                                  key={p.key}
                                  type="button"
                                  className={`acl-toggle acl-toggle--${p.key} ${sub[p.key] ? "is-on" : ""}`}
                                  onClick={() => toggleSubPermission(sectionIdx, subIdx, p.key)}
                                  aria-pressed={!!sub[p.key]}
                                  title={p.label}
                                  disabled={!canEdit}
                                >
                                  <span className="acl-toggle-knob" />
                                </button>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="acl-subrow-delete"
                              onClick={() => handleDeleteSubSection(sectionIdx, subIdx)}
                              disabled={!canEdit || subDeleting}
                              title="Remove this sub-section"
                              aria-label={`Remove ${humanize(sub.sub_section)}`}
                            >
                              {subDeleting ? (
                                <span className="acl-mini-spinner" />
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6M14 11v6" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {accessControl.length > 0 && (
          <div className="acl-footer-bar">
            <span className="acl-footer-status">
              {dirty ? "You have unsaved changes" : "All changes saved"}
            </span>
            <div className="acl-footer-actions">
              <button type="button" className="acl-btn" onClick={handleReset} disabled={!dirty}>
                Discard
              </button>
              <button
                type="button"
                className="acl-btn acl-btn--primary"
                onClick={handleSave}
                disabled={!dirty || accessControlUpdating || !canEdit}
              >
                {accessControlUpdating ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>

      <AddAccessControlModal
        open={addModalOpen}
        missingSections={missingSections}
        onCancel={() => setAddModalOpen(false)}
        onAdd={handleAddSections}
      />

      {confirmTarget && (
        <div className="acl-confirm-overlay" onClick={() => setConfirmTarget(null)}>
          <div
            className="acl-confirm-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="acl-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="acl-confirm-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
            </div>
            <h3 id="acl-confirm-title">{confirmTarget.title}</h3>
            <p>{confirmTarget.message}</p>
            <div className="acl-confirm-actions">
              <button type="button" className="acl-btn acl-btn--ghost" onClick={() => setConfirmTarget(null)}>
                Cancel
              </button>
              <button type="button" className="acl-btn acl-btn--danger" onClick={confirmDelete}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ViewFranchiseManagerAccessControl;